if (!customElements.get('wld-product-hotspots')) {
  customElements.define('wld-product-hotspots', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.points = Array.from(this.querySelectorAll('[data-hotspot]'));
      this.setupEditor();
      this.points.forEach((point) => {
        const toggle = point.querySelector('button');
        point.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.open(point);
        }, options);
        point.addEventListener('pointerleave', (event) => {
          if (event.pointerType === 'mouse' && !point.contains(document.activeElement)) this.closeAll();
        }, options);
        toggle.addEventListener('focus', () => {
          if (toggle.matches(':focus-visible')) this.open(point);
        }, options);
        point.addEventListener('focusout', (event) => {
          if (!point.contains(event.relatedTarget)) this.closeAll();
        }, options);
        toggle.addEventListener('click', (event) => {
          event.stopPropagation();
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          if (event.pointerType === 'mouse') this.open(point);
          else if (expanded) this.closeAll();
          else this.open(point);
        }, options);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          const focusedLink = document.activeElement?.closest('.wld-hotspots__label');
          focusedLink?.closest('[data-hotspot]').querySelector('button').focus();
          this.closeAll();
          event.stopPropagation();
        }
      }, options);
      document.addEventListener('pointerdown', (event) => {
        if (!this.contains(event.target) || !event.target.closest('[data-hotspot]')) this.closeAll();
      }, options);
      // Shopify selects the block's slide wrapper, so listen on that wrapper.
      this.closest('[data-wld-pm-slide]')?.addEventListener('shopify:block:select', () => {
        const slide = this.closest('[data-wld-pm-slide]');
        const root = this.closest('.shopify-section');
        const slides = Array.from(root.querySelectorAll('[data-wld-pm-slide]'));
        root.querySelector(`[data-wld-pm-thumb][data-index="${slides.indexOf(slide)}"]`)?.click();
      }, options);
    }
    setupEditor() {
      if (!this.hasAttribute('data-hotspot-design-mode') || !window.Shopify?.designMode) return;
      const launch = this.querySelector('[data-hotspot-editor]');
      const template = this.querySelector('[data-hotspot-editor-template]');
      if (!launch || !template) return;
      const options = { signal: this.events.signal };
      const baseline = this.points.map(point => ({ id: point.dataset.point, x: parseFloat(point.style.left), y: parseFloat(point.style.top) }));
      let positions = baseline.map(point => ({ ...point }));
      const storageKey = `wld-hotspot-draft:${this.dataset.editorKey}`;
      const saved = this.dataset.savedPositions || '';
      const baselineKey = JSON.stringify({ saved, baseline });
      try {
        const draft = JSON.parse(sessionStorage.getItem(storageKey));
        if (draft?.baselineKey === baselineKey && Array.isArray(draft.positions)) {
          positions = baseline.map(point => {
            const candidate = draft.positions.find(item => item.id === point.id);
            return candidate && Number.isFinite(candidate.x) && Number.isFinite(candidate.y)
              ? { ...point, x: Math.max(5, Math.min(95, candidate.x)), y: Math.max(5, Math.min(95, candidate.y)) } : point;
          });
        } else sessionStorage.removeItem(storageKey);
      } catch {}
      const dialog = template.content.firstElementChild.cloneNode(true);
      this.editorDialog = dialog;
      document.body.append(dialog);
      const canvas = dialog.querySelector('.wld-hotspot-editor__canvas');
      const image = this.querySelector('.wld-hotspots__image').cloneNode(true);
      image.removeAttribute('id');
      image.loading = 'eager';
      image.draggable = false;
      canvas.append(image);
      const output = dialog.querySelector('[data-editor-output]');
      const status = dialog.querySelector('[data-editor-status]');
      const originalLaunchText = launch.textContent;
      const dirty = () => JSON.stringify(positions) !== JSON.stringify(baseline);
      const update = () => {
        const all = new Map((saved || '').split(';').filter(Boolean).map(part => part.split(':')));
        positions.forEach(point => all.set(point.id, `${point.x},${point.y}`));
        output.value = Array.from(all, ([id, coordinates]) => `${id}:${coordinates}`).join(';');
        positions.forEach(point => {
          const marker = canvas.querySelector(`[data-editor-point="${point.id}"]`);
          marker.style.left = `${point.x}%`;
          marker.style.top = `${point.y}%`;
          const original = this.points.find(item => item.dataset.point === point.id);
          original.style.left = `${point.x}%`;
          original.style.top = `${point.y}%`;
          original.classList.toggle('wld-hotspots__point--left', point.x < 30);
          original.classList.toggle('wld-hotspots__point--right', point.x > 70);
          original.classList.toggle('wld-hotspots__point--below', point.y < 20);
        });
        status.textContent = dirty() ? status.dataset.dirty : status.dataset.ready;
        launch.textContent = originalLaunchText + (dirty() ? ' *' : '');
        try {
          if (dirty()) sessionStorage.setItem(storageKey, JSON.stringify({ baselineKey, positions }));
          else sessionStorage.removeItem(storageKey);
        } catch {}
      };
      positions.forEach(position => {
        const original = this.points.find(point => point.dataset.point === position.id);
        const name = original.querySelector('button').getAttribute('aria-label');
        const marker = document.createElement('button');
        marker.type = 'button';
        marker.className = 'wld-hotspot-editor__point';
        marker.dataset.editorPoint = position.id;
        marker.setAttribute('aria-label', `${position.id}. ${name}`);
        marker.title = name;
        const dot = document.createElement('span');
        dot.className = 'wld-hotspots__dot';
        dot.setAttribute('aria-hidden', 'true');
        const label = document.createElement('span');
        label.className = 'wld-hotspot-editor__name';
        label.textContent = name;
        marker.append(dot, label);
        canvas.append(marker);
        let dragging = null;
        const move = event => {
          const rect = canvas.getBoundingClientRect();
          if (!rect.width || !rect.height) return;
          const point = positions.find(item => item.id === position.id);
          point.x = Math.max(5, Math.min(95, Math.round((event.clientX - rect.left - dragging.offsetX) / rect.width * 100)));
          point.y = Math.max(5, Math.min(95, Math.round((event.clientY - rect.top - dragging.offsetY) / rect.height * 100)));
          update();
        };
        marker.addEventListener('pointerdown', event => {
          if (!event.isPrimary || event.button !== 0) return;
          event.preventDefault();
          marker.focus({ preventScroll: true });
          const rect = marker.getBoundingClientRect();
          dragging = { id: event.pointerId, offsetX: event.clientX - rect.left - rect.width / 2, offsetY: event.clientY - rect.top - rect.height / 2 };
          marker.setPointerCapture(event.pointerId);
          marker.classList.add('is-dragging');
        }, options);
        marker.addEventListener('pointermove', event => {
          if (!dragging || event.pointerId !== dragging.id) return;
          event.preventDefault();
          move(event);
        }, options);
        const finish = event => {
          if (!dragging || event.pointerId !== dragging.id) return;
          if (event.type === 'pointerup') move(event);
          dragging = null;
          marker.classList.remove('is-dragging');
        };
        marker.addEventListener('pointerup', finish, options);
        marker.addEventListener('pointercancel', finish, options);
        marker.addEventListener('lostpointercapture', finish, options);
        marker.addEventListener('keydown', event => {
          const directions = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] };
          if (!directions[event.key]) return;
          event.preventDefault();
          const step = event.shiftKey ? 5 : 1;
          const point = positions.find(item => item.id === position.id);
          point.x = Math.max(5, Math.min(95, point.x + directions[event.key][0] * step));
          point.y = Math.max(5, Math.min(95, point.y + directions[event.key][1] * step));
          update();
        }, options);
      });
      launch.addEventListener('click', event => {
        event.stopPropagation();
        this.closeAll();
        const source = this.querySelector('.wld-hotspots__image');
        const ratio = source.naturalWidth / source.naturalHeight || 1;
        canvas.style.width = `min(100%, ${52 * ratio}vh, 620px)`;
        update();
        dialog.showModal();
      }, options);
      dialog.querySelector('[data-editor-close]').addEventListener('click', () => dialog.close(), options);
      dialog.querySelector('[data-editor-reset]').addEventListener('click', () => {
        positions = baseline.map(point => ({ ...point }));
        update();
      }, options);
      dialog.querySelector('[data-editor-copy]').addEventListener('click', async () => {
        output.focus();
        output.select();
        try {
          const copied = document.execCommand('copy');
          if (!copied) await navigator.clipboard.writeText(output.value);
          status.textContent = status.dataset.copied;
        } catch {
          output.focus();
          output.select();
          status.textContent = status.dataset.copyFailed;
        }
      }, options);
      output.addEventListener('click', () => output.select(), options);
      update();
    }
    open(point) {
      this.closeAll();
      point.classList.add('is-open');
      point.querySelector('button').setAttribute('aria-expanded', 'true');
      point.querySelector('.wld-hotspots__label').hidden = false;
    }
    closeAll() {
      this.points?.forEach((point) => {
        point.classList.remove('is-open');
        point.querySelector('button').setAttribute('aria-expanded', 'false');
        point.querySelector('.wld-hotspots__label').hidden = true;
      });
    }
    disconnectedCallback() { this.events?.abort(); this.events = null; this.editorDialog?.remove(); this.editorDialog = null; }
  });
}

